import { useMemo } from "react";
import {
  ReactFlow, Background, MarkerType, Position, Handle,
  type Edge, type Node, type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Network, Database } from "lucide-react";
import { cls } from "../../lib/cls";
import { ExpandableGraphFrame } from "./ExpandableGraphFrame";
import type { Graph3DNode, Graph3DLink } from "./Graph3D";

/* Ontology map — typed graph of the classes FoyrMind uses (left
 * column) and the public W3C ontologies they import from (right
 * column). Each edge tells the user *which* vocab backs *which*
 * domain class — so the "where does our data really live?" question
 * is answered with a click.
 *
 * No mock data: every row is a real `NodeKind` from
 * `web/src/types/ontology.ts` paired with the W3C terms our agent's
 * TTL ontology actually imports (CLAUDE.md §4 + the ontology TTL).
 */

type ClassKind = "project" | "client" | "place" | "thing" | "actor" | "rule" | "memory" | "money" | "time";

interface DomainClass {
  id: string;          // matches NodeKind from ontology.ts
  label: string;
  kind: ClassKind;
  vocabs: readonly string[];  // ids of VOCABS below this class imports
  description: string;
}

interface Vocab {
  id: string;
  prefix: string;
  full: string;
  url: string;
  flavour: "spatial" | "provenance" | "general" | "bim" | "agent" | "units" | "people";
}

const VOCABS: readonly Vocab[] = [
  { id: "schema",   prefix: "schema:",   full: "schema.org",                              url: "https://schema.org",                          flavour: "general" },
  { id: "bot",      prefix: "bot:",      full: "Building Topology Ontology (W3C LBD)",    url: "https://w3id.org/bot",                        flavour: "spatial" },
  { id: "prov",     prefix: "prov:",     full: "PROV-O (W3C)",                            url: "https://www.w3.org/TR/prov-o/",               flavour: "provenance" },
  { id: "ifc",      prefix: "ifc:",      full: "IFC 4 (buildingSMART)",                   url: "https://standards.buildingsmart.org/IFC/",   flavour: "bim" },
  { id: "foaf",     prefix: "foaf:",     full: "FOAF (Friend of a Friend)",               url: "http://xmlns.com/foaf/spec/",                 flavour: "people" },
  { id: "qudt",     prefix: "qudt:",     full: "QUDT (Quantities · Units · Datatypes)",   url: "https://qudt.org/",                           flavour: "units" },
  { id: "gr",       prefix: "gr:",       full: "GoodRelations",                           url: "http://purl.org/goodrelations/",              flavour: "general" },
  { id: "owltime",  prefix: "time:",     full: "OWL-Time",                                url: "https://www.w3.org/TR/owl-time/",             flavour: "general" },
  { id: "agentcard",prefix: "a2a:",      full: "Google A2A AgentCard",                    url: "https://google.github.io/A2A/",               flavour: "agent" },
  { id: "mcp",      prefix: "mcp:",      full: "MCP (Anthropic)",                         url: "https://modelcontextprotocol.io/",            flavour: "agent" },
];

const CLASSES: readonly DomainClass[] = [
  { id: "Project",     label: "Project",      kind: "project", vocabs: ["schema", "prov"],            description: "schema:Project + prov:Entity. Root of every typed project graph." },
  { id: "Client",      label: "Client",       kind: "actor",   vocabs: ["foaf", "schema"],            description: "foaf:Person / foaf:Organization + schema:Person." },
  { id: "Intent",      label: "Intent",       kind: "thing",   vocabs: ["schema", "prov"],            description: "schema:Intangible — the brief turned typed." },
  { id: "Style",       label: "Style",        kind: "thing",   vocabs: ["schema"],                    description: "Style profile · ties to mood boards." },
  { id: "Room",        label: "Room",         kind: "place",   vocabs: ["bot", "ifc", "qudt"],        description: "bot:Space + ifc:Space. Area in qudt:SquareFoot." },
  { id: "Zone",        label: "Zone",         kind: "place",   vocabs: ["bot"],                       description: "bot:Zone — functional subdivision of a Room." },
  { id: "SKU",         label: "SKU",          kind: "thing",   vocabs: ["gr", "schema", "qudt"],      description: "gr:ProductOrService + schema:Product · price + lead-days qudt-typed." },
  { id: "Vendor",      label: "Vendor",       kind: "actor",   vocabs: ["gr", "schema", "foaf"],      description: "gr:BusinessEntity + schema:Organization." },
  { id: "Material",    label: "Material",     kind: "thing",   vocabs: ["schema", "qudt"],            description: "schema:Substance + qudt for tactile properties." },
  { id: "Approval",    label: "Approval",     kind: "rule",    vocabs: ["prov", "owltime"],           description: "prov:Activity + owl-time. Aged via age-days." },
  { id: "Milestone",   label: "Milestone",    kind: "time",    vocabs: ["owltime", "prov"],           description: "owl-time:Instant + prov:Activity." },
  { id: "BudgetLine",  label: "BudgetLine",   kind: "money",   vocabs: ["schema", "qudt"],            description: "schema:MonetaryAmount + qudt currency unit." },
  { id: "Rule",        label: "Rule",         kind: "rule",    vocabs: ["prov"],                      description: "prov:Plan — fires events, walks predicates." },
  { id: "MemoryCard",  label: "MemoryCard",   kind: "memory",  vocabs: ["prov", "schema"],            description: "Promoted rule with hit/reject history." },
  { id: "Alert",       label: "Alert",        kind: "rule",    vocabs: ["prov"],                      description: "prov:Activity emitted by a rule fire." },
];

const CLASS_TONE: Record<ClassKind, string> = {
  project: "ring-foyr/40       text-foyr-deep    bg-foyr/5",
  client:  "ring-amber-300     text-amber-700    bg-amber-50",
  place:   "ring-sky-300       text-sky-700      bg-sky-50",
  thing:   "ring-emerald-300   text-emerald-700  bg-emerald-50",
  actor:   "ring-violet-300    text-violet-700   bg-violet-50",
  rule:    "ring-mulberry/40   text-mulberry     bg-mulberry/5",
  memory:  "ring-amber-300     text-amber-700    bg-amber-50",
  money:   "ring-fern/40       text-fern         bg-fern/10",
  time:    "ring-fuchsia-300   text-fuchsia-700  bg-fuchsia-50",
};

const VOCAB_TONE: Record<Vocab["flavour"], string> = {
  spatial:    "ring-sky-400",
  provenance: "ring-mulberry/50",
  general:    "ring-ink-400",
  bim:        "ring-sea/50",
  agent:      "ring-foyr/50",
  units:      "ring-fuchsia-400",
  people:     "ring-amber-400",
};

function ClassNode({ data }: { data: DomainClass }): JSX.Element {
  return (
    <div className="group">
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <div
        className={cls(
          "px-3 py-2 rounded-xl ring-1 shadow-[0_1px_2px_rgba(0,0,0,0.04)] min-w-[160px]",
          CLASS_TONE[data.kind],
        )}
      >
        <div className="text-[12px] font-semibold text-ink-950 leading-tight">{data.label}</div>
        <div className="text-[10px] text-ink-500 leading-tight mt-0.5 truncate" title={data.description}>
          {data.description.split(".")[0]}
        </div>
      </div>
    </div>
  );
}

function VocabNode({ data }: { data: Vocab }): JSX.Element {
  return (
    <div className="group">
      <Handle type="target" position={Position.Left} className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <a
        href={data.url}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={cls(
          "block px-3 py-2 rounded-xl bg-cream ring-1 shadow-[0_1px_2px_rgba(0,0,0,0.04)] min-w-[200px] hover:-translate-y-0.5 transition-transform",
          VOCAB_TONE[data.flavour],
        )}
        title={`Open spec — ${data.full}`}
      >
        <div className="text-[12px] font-semibold text-ink-950 leading-tight font-mono">{data.prefix}</div>
        <div className="text-[10px] text-ink-500 leading-tight mt-0.5">{data.full}</div>
      </a>
    </div>
  );
}

const nodeTypes: NodeTypes = {
  cls: ClassNode as unknown as NodeTypes[string],
  voc: VocabNode as unknown as NodeTypes[string],
};

/** Build a typed xyflow graph from CLASSES × VOCABS.
 *  Left column = our types, right column = imported W3C vocabs.
 *  Edges follow the `vocabs:` array on each class. */
function buildOntology(): { nodes: Node[]; edges: Edge[] } {
  const colLeftX = 0;
  const colRightX = 520;
  const rowH = 78;
  const nodes: Node[] = [
    ...CLASSES.map((c, i) => ({
      id: `c-${c.id}`,
      type: "cls",
      position: { x: colLeftX, y: i * rowH },
      data: c as unknown as Record<string, unknown>,
      sourcePosition: Position.Right,
      draggable: false,
    })),
    ...VOCABS.map((v, i) => ({
      id: `v-${v.id}`,
      type: "voc",
      position: { x: colRightX, y: i * rowH * (CLASSES.length / VOCABS.length) + rowH / 4 },
      data: v as unknown as Record<string, unknown>,
      targetPosition: Position.Left,
      draggable: false,
    })),
  ];
  const edges: Edge[] = [];
  for (const c of CLASSES) {
    for (const vid of c.vocabs) {
      edges.push({
        id: `e-${c.id}-${vid}`,
        source: `c-${c.id}`,
        target: `v-${vid}`,
        animated: false,
        type: "smoothstep",
        markerEnd: { type: MarkerType.ArrowClosed, color: "#94908a", width: 14, height: 14 },
        style: { stroke: "#94908a", strokeWidth: 1.2, strokeDasharray: "3 3" },
      });
    }
  }
  return { nodes, edges };
}

export function OntologyMap(): JSX.Element {
  const { nodes, edges } = useMemo(() => buildOntology(), []);
  return (
    <section className="space-y-3">
      <header className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">
            <Network className="w-3 h-3" /> Ontology · imports
          </div>
          <h2 className="display-3 mt-0.5 text-ink-950">
            {CLASSES.length} typed classes · {VOCABS.length} W3C vocabs
          </h2>
          <p className="text-[12.5px] text-ink-500 mt-1 max-w-2xl">
            Every node FoyrMind writes is grounded in a public W3C ontology.
            Click any vocab to read its spec. Edges are real <code className="font-mono">owl:imports</code> in our TTL.
          </p>
        </div>
        <div className="text-[10.5px] text-ink-500 inline-flex items-center gap-1.5">
          <Database className="w-3 h-3" /> Stored in our typed graph store (SPARQL) · cited in every agent answer
        </div>
      </header>
      <ExpandableGraphFrame
        title="Ontology · imports"
        subtitle={`${CLASSES.length} typed classes · ${VOCABS.length} W3C vocabs · every edge is a real owl:imports`}
        graph3d={{
          nodes: [
            ...CLASSES.map<Graph3DNode>((c) => ({ id: `c-${c.id}`, label: c.label, kind: c.kind, description: c.description })),
            ...VOCABS.map<Graph3DNode>((v) => ({ id: `v-${v.id}`, label: v.prefix, kind: "thing", description: v.full })),
          ],
          links: CLASSES.flatMap<Graph3DLink>((c) =>
            c.vocabs.map((vid) => ({ source: `c-${c.id}`, target: `v-${vid}`, emphasis: "neutral" })),
          ),
        }}
      >
      <div
        className="rounded-2xl border border-border/60 bg-[radial-gradient(circle_at_20%_20%,rgba(201,92,26,0.04),transparent_70%)]"
        style={{ height: 560 }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.12 }}
          panOnDrag
          zoomOnScroll
          minZoom={0.4}
          maxZoom={1.6}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={22} size={1} color="#ece7dc" />
        </ReactFlow>
      </div>
      </ExpandableGraphFrame>
    </section>
  );
}
